import { useEffect } from 'react'

import { ArrowRight } from 'lucide-react'

import cozeeLogo from '~/images/cozee-no-bg.svg'

const NEW_APP_URL = 'https://app.getcozee.ai'

/**
 * App-wide sunset notice.
 *
 * This app is being retired, so the notice is intentionally permanent: there is
 * no close button, no Escape handler and no click-outside-to-dismiss. The only
 * way forward is the link to the new app.
 *
 * It deliberately does NOT use the Radix `Dialog` from `~/components/ui/dialog`.
 * That renders through a portal, which is client-only, so users would see the
 * old app for a beat before the notice appeared. Rendering it inline means it
 * ships in the server HTML and is on screen immediately.
 */
export default function SunsetModal() {
  useEffect(() => {
    // Stop the page behind the notice from scrolling.
    document.body.style.overflow = 'hidden'
  }, [])

  return (
    <div
      role='dialog'
      aria-modal='true'
      aria-labelledby='sunset-title'
      className='fixed inset-0 z-[100] flex items-center justify-center overflow-y-auto bg-black/80 p-4'
    >
      <div className='w-full max-w-md space-y-6 rounded-xl border bg-background p-8 text-center shadow-lg'>
        <div className='flex justify-center'>
          <div className='flex h-14 w-14 items-center justify-center rounded-xl bg-primary'>
            <img src={cozeeLogo} alt='' className='h-8 w-8' />
          </div>
        </div>

        <div className='space-y-3'>
          <h2
            id='sunset-title'
            className='text-2xl font-semibold tracking-tight'
          >
            Cozee has a new home
          </h2>

          <div className='space-y-3 text-sm leading-relaxed text-muted-foreground'>
            <p>
              Thanks for being part of the journey here. We&apos;ve rebuilt
              Cozee from the ground up, and this older version is now retired.
            </p>
            <p>
              Your dashboards, conversations, and sense of community are waiting
              for you in the new app — come on over and pick up right where you
              left off.
            </p>
          </div>
        </div>

        <a
          href={NEW_APP_URL}
          className='inline-flex h-11 w-full items-center justify-center gap-2 rounded-md bg-primary px-8 text-sm font-medium text-primary-foreground transition-all hover:bg-primary/90 active:scale-95'
        >
          Take me to the new Cozee
          <ArrowRight className='h-4 w-4' />
        </a>

        <p className='text-xs leading-relaxed text-muted-foreground'>
          Bookmark it:{' '}
          <a
            href={NEW_APP_URL}
            className='font-medium text-foreground underline underline-offset-4'
          >
            app.getcozee.ai
          </a>
          <br />
          Need a hand signing in? Reach out to your Cozee contact and we&apos;ll
          help you get settled.
        </p>
      </div>
    </div>
  )
}
